import './RejectionModal.scss'

import React, { useCallback, useEffect, useState } from 'react'
import ReactDOM from 'react-dom'

import { rejectCase } from '../../../services/notification'
import { ToastService } from '../../../services/toast.service'
import CancelConfirmModal from './CancelConfirmModal'

const CHAR_LIMIT = 225

// Fallback constants
const DEFAULT_TITLE = 'My Response on Case Resolution'
const DEFAULT_HELP_TEXT = '*Please tell us why proposed solution does not meet your needs. (Required)'
const DEFAULT_PLACEHOLDER = 'Type your comment here.'
const DEFAULT_CANCEL_LABEL = 'Cancel'
const DEFAULT_SEND_LABEL = 'Send Response'
const DEFAULT_REQUIRED_ERROR = 'This field is required.'
const DEFAULT_CONFIRM_MESSAGE = 'Are you sure you want to cancel?'
const DEFAULT_CONFIRM_YES = 'Yes'
const DEFAULT_CONFIRM_NO = 'No'
const DEFAULT_ERROR_TEXT = 'Error during the saving process.'
const DEFAULT_ERROR_SUBTEXT = 'Check your internet connection. If the problem continues feel free to contact us.'
const DEFAULT_SUCCESS_TEXT = 'Changes are saved successfully!'

export interface RejectionLabels {
  title?: string
  subjectLabel?: string
  reasonLabel?: string
  reasonPlaceholder?: string
  reasonRequiredText?: string
  caseText?: string
  confirmLabel?: string
  cancelLabel?: string
}

export interface CancelConfirmationLabels {
  message?: string
  subtext?: string
  confirmLabel?: string
  cancelLabel?: string
}

export interface RejectionModalProps {
  caseId?: string | null
  caseNumber?: string | null
  caseSubject?: string | null
  rejectionLabels?: RejectionLabels | null
  cancelConfirmationLabels?: CancelConfirmationLabels | null
  errorText?: string
  errorSubtext?: string
  successText?: string
  onClose: () => void
  onSubmit: (comment: string) => void
}

interface ResolvedLabels {
  title: string
  helpText: string
  placeholder: string
  cancelLabel: string
  sendLabel: string
  requiredError: string
  caseText?: string
  subjectLabel?: string
  confirmMessage: string
  confirmSubtext?: string
  confirmYesLabel: string
  confirmNoLabel: string
  errorText: string
  errorSubtext: string
  successText: string
}

function resolveLabels({
  rejectionLabels,
  cancelConfirmationLabels,
  errorText,
  errorSubtext,
  successText,
}: Pick<RejectionModalProps, 'rejectionLabels' | 'cancelConfirmationLabels' | 'errorText' | 'errorSubtext' | 'successText'>): ResolvedLabels {
  return {
    title:           rejectionLabels?.title              ?? DEFAULT_TITLE,
    helpText:        rejectionLabels?.reasonLabel        ?? DEFAULT_HELP_TEXT,
    placeholder:     rejectionLabels?.reasonPlaceholder  ?? DEFAULT_PLACEHOLDER,
    cancelLabel:     rejectionLabels?.cancelLabel        ?? DEFAULT_CANCEL_LABEL,
    sendLabel:       rejectionLabels?.confirmLabel       ?? DEFAULT_SEND_LABEL,
    requiredError:   rejectionLabels?.reasonRequiredText ?? DEFAULT_REQUIRED_ERROR,
    caseText:        rejectionLabels?.caseText,
    subjectLabel:    rejectionLabels?.subjectLabel,
    confirmMessage:  cancelConfirmationLabels?.message      ?? DEFAULT_CONFIRM_MESSAGE,
    confirmSubtext:  cancelConfirmationLabels?.subtext,
    confirmYesLabel: cancelConfirmationLabels?.confirmLabel ?? DEFAULT_CONFIRM_YES,
    confirmNoLabel:  cancelConfirmationLabels?.cancelLabel  ?? DEFAULT_CONFIRM_NO,
    errorText:       errorText    ?? DEFAULT_ERROR_TEXT,
    errorSubtext:    errorSubtext ?? DEFAULT_ERROR_SUBTEXT,
    successText:     successText  ?? DEFAULT_SUCCESS_TEXT,
  }
}

const RejectionModal: React.FC<RejectionModalProps> = ({
  caseId,
  caseNumber,
  caseSubject,
  rejectionLabels,
  cancelConfirmationLabels,
  errorText,
  errorSubtext,
  successText,
  onClose,
  onSubmit,
}) => {
  const [comment, setComment] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [showCancelConfirm, setShowCancelConfirm] = useState(false)

  const lbl = resolveLabels({ rejectionLabels, cancelConfirmationLabels, errorText, errorSubtext, successText })

  const handleRequestClose = () => {
    if (comment.trim()) {
      setShowCancelConfirm(true)
    } else {
      onClose()
    }
  }

  const handleEscapeKey = useCallback(() => {
    if (showCancelConfirm) { setShowCancelConfirm(false); return }
    if (comment.trim())    { setShowCancelConfirm(true);  return }
    onClose()
  }, [onClose, showCancelConfirm, comment])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') handleEscapeKey() }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleEscapeKey])

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (e.target.value.length > CHAR_LIMIT) return
    setComment(e.target.value)
    if (e.target.value.trim()) setError('')
  }

  const handleBlur = () => {
    if (!comment.trim()) setError(lbl.requiredError)
  }

  const handleSend = async () => {
    if (!comment.trim()) { setError(lbl.requiredError); return }
    setSubmitting(true)
    const success = await rejectCase(caseId ?? '', comment.trim())
    setSubmitting(false)
    if (success) {
      ToastService.success(lbl.successText)
      onSubmit(comment.trim())
      onClose()
      return
    }
    ToastService.error(lbl.errorSubtext, lbl.errorText)
  }

  const modalContent = (
    <div className="rejection-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="cop-rejection-modal-title">
      <div className="rejection-modal">

        <div className="rejection-modal__header">
          <div>
            <h2 id="cop-rejection-modal-title" className="rejection-modal__title">{lbl.title}</h2>
            {caseNumber && (
              <p className="rejection-modal__case-number">
                {lbl.caseText ? `${lbl.caseText} #${caseNumber}` : `Case #${caseNumber}`}
              </p>
            )}
          </div>
          <button type="button" className="rejection-modal__close" aria-label="Close" onClick={handleRequestClose}>
            &#x2715;
          </button>
        </div>

        {caseSubject && (
          <p className="rejection-modal__subject">
            <strong>{lbl.subjectLabel ? `${lbl.subjectLabel}:` : 'Case Subject:'}</strong> {caseSubject}
          </p>
        )}

        <p className="rejection-modal__help-text">{lbl.helpText}</p>

        <div className="rejection-modal__textarea-wrap">
          <textarea
            className={`rejection-modal__textarea${error ? ' rejection-modal__textarea--error' : ''}`}
            placeholder={lbl.placeholder}
            value={comment}
            onChange={handleChange}
            onBlur={handleBlur}
            rows={5}
            maxLength={CHAR_LIMIT}
          />
          <span className="rejection-modal__char-count">{comment.length}/{CHAR_LIMIT}</span>
        </div>

        {error && (
          <p className="rejection-modal__error" role="alert">
            <span className="rejection-modal__error-icon">i</span>
            {error}
          </p>
        )}

        <div className="rejection-modal__footer">
          <button type="button" className="rejection-modal__btn rejection-modal__btn--cancel" onClick={handleRequestClose}>
            {lbl.cancelLabel}
          </button>
          <button type="button" className="rejection-modal__btn rejection-modal__btn--send" onClick={handleSend} disabled={!comment.trim() || submitting}>
            {submitting ? 'Sending…' : lbl.sendLabel}
          </button>
        </div>

      </div>

      {showCancelConfirm && (
        <CancelConfirmModal
          message={lbl.confirmMessage}
          subtext={lbl.confirmSubtext}
          yesLabel={lbl.confirmYesLabel}
          noLabel={lbl.confirmNoLabel}
          onConfirm={onClose}
          onDismiss={() => setShowCancelConfirm(false)}
        />
      )}
    </div>
  )

  return ReactDOM.createPortal(modalContent, document.body)
}

export default RejectionModal